import type { FC, ReactNode, Ref, RefObject } from 'react'

import { BACKBOARD, RIM } from '@/lib/uploader/geometry'
import { REST_NET_PATHS } from '@/lib/uploader/net'

interface HoopBackProps {
    backboardRef: Ref<SVGGElement>
    rimRef: Ref<SVGGElement>
    netRefs: RefObject<Array<SVGPathElement | null>>
    ready: boolean
}

interface HoopFrontProps {
    rimRef: Ref<SVGGElement>
}

const RIM_LEFT = RIM.cx - RIM.rx
const RIM_RIGHT = RIM.cx + RIM.rx
const BACK_ARC = `M${RIM_LEFT} ${RIM.cy}A${RIM.rx} ${RIM.ry} 0 0 1 ${RIM_RIGHT} ${RIM.cy}`
const FRONT_ARC = `M${RIM_LEFT} ${RIM.cy}A${RIM.rx} ${RIM.ry} 0 0 0 ${RIM_RIGHT} ${RIM.cy}`

// Everything that sits behind a file entering the hoop: backboard, back of the rim, the net.
export const HoopBack: FC<HoopBackProps> = ({ backboardRef, rimRef, netRefs, ready }): ReactNode => {
    return (
        <svg width={540} height={675} viewBox='0 0 540 675' className='absolute left-0 top-0 overflow-visible' aria-hidden='true'>
            <g ref={backboardRef}>
                <rect
                    x={BACKBOARD.x}
                    y={BACKBOARD.y}
                    width={BACKBOARD.width}
                    height={BACKBOARD.height}
                    rx={3}
                    className='fill-none stroke-hoop'
                    strokeWidth={2}
                />
                <rect x={247} y={318} width={46} height={32} rx={2} className='fill-none stroke-hoop' strokeWidth={1.5} />
                <rect x={266} y={358} width={8} height={12} rx={1.5} className='fill-hoop' />
            </g>

            <g ref={rimRef}>
                <path d={BACK_ARC} className='fill-none stroke-hoop' strokeWidth={4} strokeLinecap='round' />
            </g>

            <g className={ready ? 'nbn-intro-net fill-none stroke-mesh' : 'fill-none stroke-mesh'} strokeWidth={1} strokeLinecap='round' strokeLinejoin='round'>
                {REST_NET_PATHS.map((path, index) => (
                    <path
                        key={index}
                        d={path}
                        ref={element => {
                            netRefs.current[index] = element
                        }}
                    />
                ))}
            </g>
        </svg>
    )
}

// The front lip of the rim, the only part of the hoop drawn in front of a file going through.
export const HoopFront: FC<HoopFrontProps> = ({ rimRef }): ReactNode => {
    return (
        <svg width={540} height={675} viewBox='0 0 540 675' className='absolute left-0 top-0 overflow-visible' aria-hidden='true'>
            <defs>
                <linearGradient id='nbn-rim-lip' gradientUnits='userSpaceOnUse' x1={RIM_LEFT} y1={0} x2={RIM_RIGHT} y2={0}>
                    <stop offset='0' style={{ stopColor: 'var(--color-hoop, #E8622A)' }} />
                    <stop offset='0.5' style={{ stopColor: 'var(--color-hoop-deep, #D4521C)' }} />
                    <stop offset='1' style={{ stopColor: 'var(--color-hoop, #E8622A)' }} />
                </linearGradient>
            </defs>

            <g ref={rimRef}>
                <path d={FRONT_ARC} fill='none' stroke='url(#nbn-rim-lip)' strokeWidth={4} strokeLinecap='round' />
            </g>
        </svg>
    )
}
